import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import {
  Box,
  IconButton,
  MenuItem,
  Popover,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useZoom } from 'context/ZoomContext';
import { useFilterPopover } from 'dashboard409a/hooks/useFilterPopover';
import { useModelFilters } from 'dashboard409a/hooks/useModelFilters';
import { ModelFiltersProps } from './filters.props';
import { modelFiltersStyles } from './ModelFilters.styles';
import { UserAutocomplete } from './UserAutocomplete';

export const ModelFilters = ({ statusOptions, statusOptionsLoading, onFiltersChange }: ModelFiltersProps) => {
  const { handleOpen, popoverProps } = useFilterPopover({
    popoverId: 'model-filters-popover',
    anchorOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
    minHeight: '100px',
  });
  
  const { zoomLevel } = useZoom();

  const {
    searchParam,
    searchStatus,
    fromDueDate,
    toDueDate,
    selectedPreparers,
    selectedReviewers,
    preparerSearchInput,
    reviewerSearchInput,
    sortedPreparers,
    sortedReviewers,
    isLoadingPreparers,
    isLoadingReviewers,
    handleSearchParamChange,
    handleSearchStatusChange,
    handleFromDueDateChange,
    handleToDueDateChange,
    handlePreparersChange,
    handleReviewersChange,
    setPreparerSearchInput,
    setReviewerSearchInput,
    cleanFilters,
  } = useModelFilters({ onFiltersChange });

  return (
    <>
      <Tooltip title="Filters" arrow>
        <IconButton onClick={handleOpen}>
          <FilterAltIcon />
        </IconButton>
      </Tooltip>
      <Popover {...popoverProps}>
        <Box display="flex" alignItems="center" gap={1} sx={modelFiltersStyles.popoverContent}>
          <TextField size="small" label="Search" variant="outlined" value={searchParam} onChange={handleSearchParamChange}/>
          <Select
            size="small"
            label="Status"
            variant="outlined"
            value={searchStatus}
            onChange={handleSearchStatusChange}
            sx={modelFiltersStyles.selectStatus}>
            <MenuItem disabled value="default">
        Select Status
            </MenuItem>
            <MenuItem value="All">All</MenuItem>
            {statusOptionsLoading ? (
              <MenuItem disabled> Loading... </MenuItem>
            ) : (
              statusOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))
            )}
          </Select>
          <DatePicker
            label="From"
            value={fromDueDate}
            onChange={handleFromDueDateChange}
            slotProps={{
              textField: {
                variant: 'outlined',
                size: 'small',
              },
            }}
          />
          <DatePicker
            label="To"
            value={toDueDate}
            onChange={handleToDueDateChange}
            minDate={fromDueDate || undefined}
            slotProps={{
              textField: {
                variant: 'outlined',
                size: 'small',
              },
            }}
          />
          <UserAutocomplete
            options={sortedReviewers}
            value={selectedReviewers}
            onChange={handleReviewersChange}
            onInputChange={(_event, newInputValue) => {
              setReviewerSearchInput(newInputValue);
            }}
            inputValue={reviewerSearchInput}
            loading={isLoadingReviewers}
            label="Select Reviewers"
            placeholder="Search for reviewers..."
            zoomLevel={zoomLevel}
            textFieldSx={modelFiltersStyles.autocompleteTextField}
          />
          <UserAutocomplete
            options={sortedPreparers}
            value={selectedPreparers}
            onChange={handlePreparersChange}
            onInputChange={(_event, newInputValue) => {
              setPreparerSearchInput(newInputValue);
            }}
            inputValue={preparerSearchInput}
            loading={isLoadingPreparers}
            label="Select Preparers"
            placeholder="Search for preparers..."
            zoomLevel={zoomLevel}
            textFieldSx={modelFiltersStyles.preparersTextField}
          />
          <Tooltip title="Clear filters">
            <IconButton onClick={cleanFilters}>
              <FilterAltOffIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Popover>
    </>
  );
};
